import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  resendVerificationRequestSchema,
  type ResendVerificationRequest,
} from "@nh/shared";
import {
  Alert,
  EmailField,
  Form,
  FormError,
  SubmitButton,
  type ButtonVariant,
} from "@/shared/ui/core";
import { useCountdown } from "@/shared/lib/useCountdown";
import { resendVerificationRequest } from "../auth.api";
import { RESEND_COOLDOWN_SECONDS, waitLabel } from "../auth.cooldown";
import { useAuthSubmit } from "../useAuthSubmit";

/**
 * El mensaje de confirmación. Es UNO SOLO, y es deliberado.
 *
 * ⚠️ NO lo cambies por "te reenviamos el enlace" ni por "esa cuenta ya
 * está verificada". Este texto es idéntico exista o no el correo y esté o
 * no verificada la cuenta, porque el servidor responde idéntico en los
 * tres casos (ver `resendVerificationResponseSchema`). Si esta pantalla
 * los distinguiera, cualquiera podría escribir correos uno por uno y usarla
 * como buscador de quién tiene cuenta — y en un negocio de red, la lista de
 * quién está dentro es justo lo que querría un competidor.
 *
 * El "Si ese correo tiene una cuenta" no es timidez de redacción: es la
 * única frase honesta que se puede decir sin saber —ni poder saber— cuál
 * de los casos ocurrió.
 */
const CONFIRMATION_MESSAGE =
  "Si ese correo tiene una cuenta sin verificar, te enviamos un enlace " +
  "nuevo. Revisa tu bandeja y el correo no deseado.";

export interface ResendVerificationProps {
  /**
   * El correo, cuando la pantalla YA lo sabe.
   *
   * El login lo sabe: acaba de intentar entrar con él. Volvérselo a pedir
   * sería hacerle escribir dos veces lo mismo en la misma pantalla, así
   * que ahí el campo no se pinta y el botón manda directo.
   *
   * `/verificar` NO lo sabe: un token que el servidor rechazó no dice de
   * quién era —ese es justamente el punto de rechazarlo—, así que ahí hay
   * que preguntarlo.
   *
   * En los dos casos el correo vive en el mismo formulario y pasa por el
   * mismo esquema: sin campo montado, react-hook-form conserva igual el
   * valor por defecto, y `zodResolver` lo normaliza igual antes de
   * enviarlo. No hay dos caminos de envío, solo un campo que se pinta o no.
   */
  email?: string;
  /** Texto del botón la primera vez. Después siempre es "Reenviar el enlace". */
  submitLabel?: string;
  /**
   * Por qué se está ofreciendo esto, cuando la razón no está ya escrita en
   * la pantalla.
   *
   * Se pinta EN EL SITIO donde después irá la confirmación, y desaparece
   * cuando esta llega. No es ahorro de píxeles: una vez enviado el correo,
   * "te falta verificar" y "si ese correo tiene cuenta, te mandamos un
   * enlace" dicen lo mismo dos veces, y dos avisos apilados obligan a leer
   * ambos para averiguar cuál manda.
   *
   * `/verificar` no lo usa: ahí el motivo del fallo lo cuenta la pantalla
   * y debe seguir visible después del reenvío, porque explica por qué se
   * llegó hasta aquí.
   */
  explanation?: ReactNode;
  /**
   * Cuánto peso visual tiene el botón.
   *
   * `primary` donde reenviar es LA acción de la pantalla (el aterrizaje
   * fallido de `/verificar`: no hay otra cosa que hacer ahí).
   *
   * `secondary` donde compite con otra: en el login, "Iniciar sesión" sigue siendo
   * lo que la mayoría viene a hacer, y dos botones morados idénticos uno
   * encima del otro obligan a leerlos para saber cuál es cuál.
   */
  variant?: ButtonVariant;
}

/**
 * Pedir (otra vez) el enlace de verificación del correo.
 *
 * Aparece en dos sitios —el aterrizaje fallido de `/verificar` y el login
 * cuando el servidor rechaza por correo sin verificar— y en los dos tiene
 * que comportarse igual: mismo mensaje ciego, misma espera de
 * {@link RESEND_COOLDOWN_SECONDS} segundos. Por eso es un componente y no
 * dos copias: una copia que se quede sin la espera manda tres correos por
 * clic nervioso, y una que "mejore" el mensaje delata quién tiene cuenta.
 */
export function ResendVerification({
  email: knownEmail,
  submitLabel = "Enviar el enlace",
  explanation,
  variant = "primary",
}: ResendVerificationProps) {
  const form = useForm<ResendVerificationRequest>({
    resolver: zodResolver(resendVerificationRequestSchema),
    defaultValues: { email: knownEmail ?? "" },
    mode: "onSubmit",
  });

  const [sent, setSent] = useState(false);
  const cooldown = useCountdown();

  const { submit, formError, dismissFormError } = useAuthSubmit({
    form,
    // Un fallo de validación que el servidor señale sobre `email` sí es del
    // campo (un correo malformado que se coló). Lo que NUNCA manda es "esa
    // cuenta no existe": no hay código para eso, a propósito.
    fields: ["email"],
    action: async (values) => {
      await resendVerificationRequest(values);
      setSent(true);
    },
    onSuccess: () => cooldown.start(RESEND_COOLDOWN_SECONDS),
  });

  const waiting = cooldown.isRunning;

  return (
    <div className="grid gap-3">
      {/* Fuera del `<Form>`: no es un campo, es el resultado del último
          envío — y sirve igual al primero que al reenvío. */}
      <FormError error={formError} onDismiss={dismissFormError} className="mb-0" />

      {sent ? <Alert variant="success">{CONFIRMATION_MESSAGE}</Alert> : explanation}

      <Form form={form} onSubmit={submit} className="grid gap-3">
        {!knownEmail && <EmailField purpose="account" />}
        <SubmitButton variant={variant} size="lg" shape="rounded" fullWidth disabled={waiting}>
          {waiting
            ? waitLabel(cooldown.remaining)
            : sent
              ? "Reenviar el enlace"
              : submitLabel}
        </SubmitButton>
      </Form>
    </div>
  );
}
